import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Which website the console is currently looking at.
 *
 * PaymentFlow is one pool serving many participating websites, and every operator screen is
 * platform-wide by construction: a queue is worked in the order money arrived, not site by site.
 * That default is right for the work and wrong for the question that comes up whenever something
 * looks off — "is this *one* site broken, or is it everything?" — which a mixed pool cannot answer
 * at all.
 *
 * So the scope is a property of the console session rather than of a screen. One selector in the
 * shell, and every read narrows together: a dashboard tile, the queue beneath it and the history
 * behind it all describe the same site or all describe the platform, never a mixture. That is the
 * whole reason this lives in a context consumed inside the query hooks (`lib/api/ops-queries.ts`)
 * instead of as a filter each page wires up for itself — a page cannot forget to apply it, and a
 * page added later gets it without knowing it exists.
 *
 * Three properties are decisions rather than consequences:
 *
 * - **The whole pool is the default, and stays the default.** {@link ALL_WEBS} sends no parameter
 *   at all, so an unscoped console issues byte-identical requests to the ones it issued before
 *   this existed. Nobody inherits a filter they did not set.
 * - **It survives a reload, and it is not in the URL.** A scope is a preference — where an
 *   operator is working today — not a place, and `lib/nav/list-state.ts` is deliberately strict
 *   that a param in the address bar is a filter *that screen* offers. Putting a console-wide
 *   preference there would put the same value on eleven screens' contracts. It lives in
 *   `localStorage` instead, so a link an operator pastes to a colleague still means what that
 *   colleague's own console shows them.
 * - **An id is never validated against the directory here.** A site that is renamed, suspended or
 *   removed leaves a stored id that matches nothing, and every endpoint answers that with an empty
 *   page rather than an error. The selector renders such a value as an unknown scope the operator
 *   can clear, which is honest; silently resetting to the whole pool would show them platform-wide
 *   numbers while they believed they were looking at one site.
 */

/** The scope meaning "every website" — the unfiltered pool the console has always shown. */
export const ALL_WEBS = 'ALL';

/** A participant id, or {@link ALL_WEBS}. */
export type WebScope = string;

/** Where the choice is remembered between sessions. Exported so tests seed the real key. */
export const WEB_SCOPE_STORAGE_KEY = 'pf.console.web-scope';

/** What {@link useWebScope} hands a caller. */
interface WebScopeHandle {
  /** The current scope: a participant id, or {@link ALL_WEBS}. */
  readonly scope: WebScope;
  /**
   * The scope as the wire wants it: a participant id, or `undefined` for the whole pool.
   *
   * `undefined` rather than `'ALL'` because `buildUrl` drops undefined params — so the unscoped
   * request carries no `participant` at all, instead of one naming a participant called "ALL".
   */
  readonly participant: string | undefined;
  /** Change the scope. Remembered for the next session. */
  readonly setScope: (next: WebScope) => void;
}

const WebScopeContext = createContext<WebScopeHandle | null>(null);

/** Read the remembered scope. Storage can throw (private mode, disabled cookies); that is fine. */
function readStoredScope(): WebScope {
  try {
    const stored = globalThis.localStorage?.getItem(WEB_SCOPE_STORAGE_KEY);
    // Length-capped for the same reason the URL readers cap theirs: this string is concatenated
    // into a request, and `participant.id` is 64 characters wide at its widest.
    return stored !== null && stored !== undefined && stored.length > 0 && stored.length <= 64
      ? stored
      : ALL_WEBS;
  } catch {
    return ALL_WEBS;
  }
}

/** Remember the scope, if this browser lets us. Failing to persist must not fail the click. */
function storeScope(scope: WebScope): void {
  try {
    globalThis.localStorage?.setItem(WEB_SCOPE_STORAGE_KEY, scope);
  } catch {
    // Ignored: the scope still applies for this session, which is what the operator just asked for.
  }
}

/**
 * Provides the console-wide website scope.
 *
 * Mounted once, around the whole app, so the operator shell's selector and the query hooks under
 * it read the same value. Tests that mount a single page through `AppProviders` get it too — which
 * is deliberate: a test wrapping its component in a different provider stack tests a different
 * application than the one that ships.
 */
export function WebScopeProvider({ children }: { children: ReactNode }): ReactNode {
  const [scope, setScopeState] = useState<WebScope>(readStoredScope);

  const setScope = useCallback((next: WebScope) => {
    setScopeState(next);
    storeScope(next);
  }, []);

  const value = useMemo<WebScopeHandle>(
    () => ({ scope, participant: scope === ALL_WEBS ? undefined : scope, setScope }),
    [scope, setScope],
  );

  return <WebScopeContext.Provider value={value}>{children}</WebScopeContext.Provider>;
}

/**
 * The website the console is scoped to.
 *
 * Safe outside a provider: it reports the whole pool. A query hook is used in more places than the
 * operator shell wraps — the customer cashier mounts some of the same plumbing — and throwing
 * there would turn a missing preference into a blank screen.
 */
export function useWebScope(): WebScopeHandle {
  const value = useContext(WebScopeContext);
  return value ?? UNSCOPED;
}

/** The fallback handle: the whole pool, and a setter that has nowhere to write. */
const UNSCOPED: WebScopeHandle = {
  scope: ALL_WEBS,
  participant: undefined,
  setScope: () => undefined,
};

/**
 * A query key narrowed by the current scope.
 *
 * The unscoped key is returned *unchanged*, which is what keeps this additive: a console that
 * never leaves the whole pool caches under exactly the keys it did before, and the scoped views
 * are extra entries beside it rather than a re-keying of everything. Switching sites is then a
 * cache miss and a fetch — correct, because the answer really is different data — while switching
 * back is a hit.
 */
export function webScopedKey(
  key: readonly unknown[],
  participant: string | undefined,
): readonly unknown[] {
  return participant === undefined ? key : [...key, { web: participant }];
}
